import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';
import { getEquipmentExpenseDetails, updateEquipmentExpense } from '../../../../redux/Slices/EquipmentExpensesSlice';
import { getBanks, getSafes } from '../../../../redux/Slices/authSlice';
import { getEquipments } from '../../../../redux/Slices/EquipmentSlice';
import { getProjects } from '../../../../redux/Slices/ProjectsSlice';
import axios from 'axios';
import { BASE_URL, getToken } from '../../../../shared/utils.js/utils';

const normalizeListResponse = (payload) => {
  if (!payload) return { items: [], pagination: null };
  
  let items = [];
  let pagination = null;
  
  if (Array.isArray(payload?.data?.data)) {
    items = payload.data.data;
    pagination = payload.data?.meta || payload.data?.pagination || null;
  } else if (Array.isArray(payload?.data)) {
    items = payload.data;
    pagination = payload.meta || payload.pagination || null;
  } else if (Array.isArray(payload?.items)) {
    items = payload.items;
    pagination = payload.pagination || payload.meta || null;
  } else if (Array.isArray(payload)) {
    items = payload;
    pagination = null;
  } else {
    items = [];
    pagination = payload.pagination || payload.meta || null;
  }
  
  if (pagination) {
    pagination = {
      current_page: pagination.current_page || pagination.page || 1,
      last_page: pagination.last_page || pagination.total_pages || 1,
      per_page: pagination.per_page || 10,
      total: pagination.total || 0,
    };
  }
  
  return { items, pagination };
};

// Edit Equipment Expenses Component
const EditEquipmentExpenses = () => {
  const { t } = useTranslation();
  const { equipmentId, expenseId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { banks, banksPagination, safes, safesPagination } = useSelector((state) => state.auth);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const isRTL = i18n.language === 'ar';
  const [form, setForm] = useState({
    equipment_id: '',
    expense_date: '',
    amount: '',
    description_ar: '',
    description_en: '',
    payment_account_type: '', // 'bank' or 'safe'
    payment_account_id: '',
    project_id: '',
    notes: '',
  });

  // Equipment dropdown
  const [equipmentDropdownOpen, setEquipmentDropdownOpen] = useState(false);
  const [equipmentSearchTerm, setEquipmentSearchTerm] = useState('');
  const [equipmentOptions, setEquipmentOptions] = useState([]);
  const [equipmentPage, setEquipmentPage] = useState(1);
  const [equipmentPagination, setEquipmentPagination] = useState(null);
  const equipmentSearchInputRef = useRef(null);

  // Project dropdown
  const [projectDropdownOpen, setProjectDropdownOpen] = useState(false);
  const [projectSearchTerm, setProjectSearchTerm] = useState('');
  const [projectOptions, setProjectOptions] = useState([]);
  const [projectPage, setProjectPage] = useState(1);
  const [projectPagination, setProjectPagination] = useState(null);
  const projectSearchInputRef = useRef(null);

  // Bank/Safe dropdown
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const [accountSearchTerm, setAccountSearchTerm] = useState('');
  const [accountPage, setAccountPage] = useState(1);
  const accountSearchInputRef = useRef(null);
  const accountSearchTimeoutRef = useRef(null);

  useEffect(() => {
    const fetchDetails = async () => {
      try {
        setIsLoading(true);
        const payload = await dispatch(
          getEquipmentExpenseDetails({ equipmentId, expenseId })
        ).unwrap();
        const details = payload?.data || payload;
        
        // Determine payment account type from account_id
        // We need to check if it's a bank or safe by fetching both and checking
        let paymentAccountType = '';
        let paymentAccountId = '';
        
        if (details.payment_account_id) {
          // Try to determine type - this is a simplified approach
          // In a real scenario, the API should return the account type
          paymentAccountId = String(details.payment_account_id);
        }

        const equipmentIdValue = String(details.equipment_id || equipmentId);
        const projectIdValue = details.project_id ? String(details.project_id) : '';

        setForm({
          equipment_id: equipmentIdValue,
          expense_date: details.expense_date ? details.expense_date.split('T')[0] : '',
          amount: details.amount || '',
          description_ar: details.description_ar || '',
          description_en: details.description_en || '',
          payment_account_type: paymentAccountType,
          payment_account_id: paymentAccountId,
          project_id: projectIdValue,
          notes: details.notes || '',
        });

        // Fetch selected equipment and project to display their names
        const fetchSelectedItems = async () => {
          if (equipmentIdValue) {
            try {
              const token = getToken();
              const equipmentResponse = await axios.get(`${BASE_URL}/equipment/${equipmentIdValue}`, {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Accept-Language': i18n.language,
                },
              });
              const equipmentData = equipmentResponse.data?.data || equipmentResponse.data;
              if (equipmentData) {
                setEquipmentOptions((prev) => {
                  const exists = prev.find((e) => String(e.id) === String(equipmentData.id));
                  return exists ? prev : [...prev, equipmentData];
                });
              }
            } catch (error) {
              console.error('Failed to fetch equipment:', error);
            }
          }

          if (projectIdValue) {
            try {
              const token = getToken();
              const projectResponse = await axios.get(`${BASE_URL}/projects/${projectIdValue}`, {
                headers: {
                  Authorization: `Bearer ${token}`,
                  'Accept-Language': i18n.language,
                },
              });
              const projectData = projectResponse.data?.data || projectResponse.data;
              if (projectData) {
                setProjectOptions((prev) => {
                  const exists = prev.find((p) => String(p.id) === String(projectData.id));
                  return exists ? prev : [...prev, projectData];
                });
              }
            } catch (error) {
              console.error('Failed to fetch project:', error);
            }
          }
        };
        
        fetchSelectedItems();
      } catch (error) {
        toast.error(error || t('equipmentExpenses.edit.messages.fetchError'), { rtl: isRTL });
        navigate('/equipment-expenses');
      } finally {
        setIsLoading(false);
      }
    };
    fetchDetails();
  }, [dispatch, equipmentId, expenseId, navigate, t, isRTL]);

  const fetchEquipments = useCallback(
    async (page = equipmentPage, search = equipmentSearchTerm) => {
      try {
        const token = getToken();
        if (!token) throw new Error(t('equipmentExpenses.messages.noAuthToken'));
        const response = await axios.get(`${BASE_URL}/equipment`, {
          params: {
            page,
            per_page: 10,
            search: search || undefined,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            'Accept-Language': i18n.language,
          },
        });
        const { items, pagination: paginationMeta } = normalizeListResponse(response.data);
        setEquipmentOptions(items);
        setEquipmentPagination(paginationMeta);
      } catch (error) {
        toast.error(error?.response?.data?.message || t('equipmentExpenses.messages.fetchEquipmentsError'), { rtl: isRTL });
      }
    },
    [t, isRTL]
  );

  const fetchProjects = useCallback(
    async (page = projectPage, search = projectSearchTerm) => {
      try {
        const token = getToken();
        if (!token) throw new Error(t('equipmentExpenses.messages.noAuthToken'));
        const response = await axios.get(`${BASE_URL}/projects`, {
          params: {
            page,
            per_page: 10,
            search: search || undefined,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            'Accept-Language': i18n.language,
          },
        });
        const { items, pagination: paginationMeta } = normalizeListResponse(response.data);
        setProjectOptions(items);
        setProjectPagination(paginationMeta);
      } catch (error) {
        toast.error(error?.response?.data?.message || t('equipmentExpenses.messages.fetchProjectsError'), { rtl: isRTL });
      }
    },
    [t, isRTL]
  );

  useEffect(() => {
    if (equipmentDropdownOpen) {
      fetchEquipments(equipmentPage, equipmentSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentDropdownOpen, equipmentPage, equipmentSearchTerm]);

  useEffect(() => {
    if (projectDropdownOpen) {
      fetchProjects(projectPage, projectSearchTerm);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectDropdownOpen, projectPage, projectSearchTerm]);

  useEffect(() => {
    if (equipmentSearchTerm !== undefined && equipmentDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setEquipmentPage(1);
        fetchEquipments(1, equipmentSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [equipmentSearchTerm, equipmentDropdownOpen]);

  useEffect(() => {
    if (projectSearchTerm !== undefined && projectDropdownOpen) {
      const timeoutId = setTimeout(() => {
        setProjectPage(1);
        fetchProjects(1, projectSearchTerm);
      }, 300);
      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [projectSearchTerm, projectDropdownOpen]);

  // Fetch banks or safes based on payment_account_type
  useEffect(() => {
    if (accountDropdownOpen && form.payment_account_type) {
      if (form.payment_account_type === 'bank') {
        dispatch(getBanks({ page: accountPage, per_page: 10, search: accountSearchTerm || undefined }));
      } else if (form.payment_account_type === 'safe') {
        dispatch(getSafes({ page: accountPage, per_page: 10, search: accountSearchTerm || undefined }));
      }
    }
  }, [accountDropdownOpen, form.payment_account_type, accountPage, accountSearchTerm, dispatch]);

  // Search banks/safes with debounce
  useEffect(() => {
    if (accountSearchTerm !== undefined && accountDropdownOpen && form.payment_account_type) {
      if (accountSearchTimeoutRef.current) clearTimeout(accountSearchTimeoutRef.current);
      accountSearchTimeoutRef.current = setTimeout(() => {
        setAccountPage(1);
        if (form.payment_account_type === 'bank') {
          dispatch(getBanks({ page: 1, per_page: 10, search: accountSearchTerm || undefined }));
        } else if (form.payment_account_type === 'safe') {
          dispatch(getSafes({ page: 1, per_page: 10, search: accountSearchTerm || undefined }));
        }
      }, 300);
    }
    return () => {
      if (accountSearchTimeoutRef.current) clearTimeout(accountSearchTimeoutRef.current);
    };
  }, [accountSearchTerm, accountDropdownOpen, form.payment_account_type, dispatch]);

  // Close dropdowns when clicking outside or opening another dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      const target = event.target;
      const isDropdownContainer = target.closest('[data-dropdown-container]');
      const isDropdownToggle = target.closest('[data-dropdown-toggle]');
      
      if (!isDropdownContainer && !isDropdownToggle) {
        if (equipmentDropdownOpen) setEquipmentDropdownOpen(false);
        if (projectDropdownOpen) setProjectDropdownOpen(false);
        if (accountDropdownOpen) setAccountDropdownOpen(false);
      }
    };
    
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [equipmentDropdownOpen, projectDropdownOpen, accountDropdownOpen]);

  // Close other dropdowns when opening one
  useEffect(() => {
    if (equipmentDropdownOpen) {
      if (projectDropdownOpen) setProjectDropdownOpen(false);
      if (accountDropdownOpen) setAccountDropdownOpen(false);
    }
  }, [equipmentDropdownOpen]);

  useEffect(() => {
    if (projectDropdownOpen) {
      if (equipmentDropdownOpen) setEquipmentDropdownOpen(false);
      if (accountDropdownOpen) setAccountDropdownOpen(false);
    }
  }, [projectDropdownOpen]);

  useEffect(() => {
    if (accountDropdownOpen) {
      if (equipmentDropdownOpen) setEquipmentDropdownOpen(false);
      if (projectDropdownOpen) setProjectDropdownOpen(false);
    }
  }, [accountDropdownOpen]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'payment_account_type') {
    setForm((prev) => ({
      ...prev,
        payment_account_type: value,
        payment_account_id: '', // Reset when type changes
    }));
      setAccountSearchTerm('');
      setAccountPage(1);
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.equipment_id) {
      toast.error(t('equipmentExpenses.edit.messages.equipmentRequired'), { rtl: isRTL });
      return;
    }
    if (!form.expense_date || !form.amount || !form.description_ar || !form.description_en) {
      toast.error(t('equipmentExpenses.edit.messages.fieldsRequired'), { rtl: isRTL });
      return;
    }
    if (!form.payment_account_type || !form.payment_account_id) {
      toast.error(t('equipmentExpenses.edit.messages.paymentAccountRequired'), { rtl: isRTL });
      return;
    }
    try {
      setIsSubmitting(true);
      await dispatch(
        updateEquipmentExpense({
          equipmentId: form.equipment_id,
          expenseId,
          expenseData: {
            expense_date: form.expense_date,
            amount: Number(form.amount),
            description_ar: form.description_ar,
            description_en: form.description_en,
            payment_account_id: Number(form.payment_account_id),
            project_id: form.project_id ? Number(form.project_id) : null,
            notes: form.notes || null,
          },
        })
      ).unwrap();
      toast.success(t('equipmentExpenses.edit.messages.updateSuccess'), { rtl: isRTL });
      navigate('/equipment-expenses');
    } catch (error) {
      toast.error(error || t('equipmentExpenses.edit.messages.updateError'), { rtl: isRTL });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getAccountOptions = () => {
    if (form.payment_account_type === 'bank') {
      return Array.isArray(banks) ? banks : [];
    } else if (form.payment_account_type === 'safe') {
      return Array.isArray(safes) ? safes : [];
    }
    return [];
  };

  const getAccountPagination = () => {
    if (form.payment_account_type === 'bank') {
      return banksPagination || { current_page: 1, last_page: 1 };
    } else if (form.payment_account_type === 'safe') {
      return safesPagination || { current_page: 1, last_page: 1 };
    }
    return { current_page: 1, last_page: 1 };
  };

  const getSelectedAccount = () => {
    if (!form.payment_account_id) return null;
    const options = getAccountOptions();
    return options.find((item) => String(item.id) === String(form.payment_account_id));
  };

  if (isLoading) {
    return (
      <div style={{ ...pageStyle, direction: isRTL ? 'rtl' : 'ltr' }}>
        <div style={{ ...cardStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>{t('equipmentExpenses.edit.messages.fetchError')}</div>
      </div>
    );
  }

  return (
    <div style={{ ...pageStyle, direction: isRTL ? 'rtl' : 'ltr' }}>
      <div style={{ ...cardStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
        <h2 style={{ marginBottom: 20, fontSize: '20px' }}>{t('equipmentExpenses.edit.title')}</h2>
        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ position: 'relative' }} data-dropdown-container>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
              <span style={{ fontSize: 14 }}>{t('equipmentExpenses.edit.equipment')}</span>
              <div data-dropdown-toggle onClick={() => setEquipmentDropdownOpen((prev) => !prev)} style={{ ...dropdownDisplayStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                <span>
                  {form.equipment_id
                    ? (isRTL
                    ? equipmentOptions.find((e) => String(e.id) === String(form.equipment_id))?.name_ar ||
                      equipmentOptions.find((e) => String(e.id) === String(form.equipment_id))?.name_en ||
                          `${t('equipmentExpenses.tableHeaders.equipment')} #${form.equipment_id}`
                        : equipmentOptions.find((e) => String(e.id) === String(form.equipment_id))?.name_en ||
                          equipmentOptions.find((e) => String(e.id) === String(form.equipment_id))?.name_ar ||
                          `${t('equipmentExpenses.tableHeaders.equipment')} #${form.equipment_id}`)
                    : t('equipmentExpenses.edit.selectEquipment')}
                </span>
                <span style={{ fontSize: 12 }}>▼</span>
              </div>
              <DropdownWithSearch
                isOpen={equipmentDropdownOpen}
                searchTerm={equipmentSearchTerm}
                onSearchChange={setEquipmentSearchTerm}
                searchInputRef={equipmentSearchInputRef}
                items={equipmentOptions}
                pagination={equipmentPagination}
                onPageChange={setEquipmentPage}
                onClose={() => setEquipmentDropdownOpen(false)}
                onSelect={(item) => {
                  setForm((prev) => ({ ...prev, equipment_id: String(item.id) }));
                  setEquipmentDropdownOpen(false);
                  setEquipmentSearchTerm('');
                  setEquipmentPage(1);
                }}
                label={t('equipmentExpenses.tableHeaders.equipment')}
              />
            </label>
          </div>

          <div style={gridStyle}>
            <Input label={t('equipmentExpenses.edit.expenseDate')} type="date" name="expense_date" value={form.expense_date} onChange={handleChange} required isRTL={isRTL} />
            <Input label={t('equipmentExpenses.edit.amount')} type="number" min="0"  name="amount" value={form.amount} onChange={handleChange} required isRTL={isRTL} />
          </div>

          <div style={gridStyle}>
            {isRTL ? (
              <>
                <Input label={t('equipmentExpenses.edit.descriptionAr')} name="description_ar" value={form.description_ar} onChange={handleChange} required isRTL={true} />
                <Input label={t('equipmentExpenses.edit.descriptionEn')} name="description_en" value={form.description_en} onChange={handleChange} required isRTL={false} />
              </>
            ) : (
              <>
                <Input label={t('equipmentExpenses.edit.descriptionEn')} name="description_en" value={form.description_en} onChange={handleChange} required isRTL={false} />
                <Input label={t('equipmentExpenses.edit.descriptionAr')} name="description_ar" value={form.description_ar} onChange={handleChange} required isRTL={true} />
              </>
            )}
          </div>

          <div style={{ position: 'relative' }}>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
              <span style={{ fontSize: 14 }}>{t('equipmentExpenses.edit.paymentAccountType')}</span>
              <select
                name="payment_account_type"
                value={form.payment_account_type}
                onChange={handleChange}
                style={{ ...filterInputStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}
                required
              >
                <option value="">{t('equipmentExpenses.edit.selectPaymentAccountType')}</option>
                <option value="bank">{t('equipmentExpenses.edit.bank')}</option>
                <option value="safe">{t('equipmentExpenses.edit.safe')}</option>
              </select>
            </label>
          </div>

          {form.payment_account_type && (
            <div style={{ position: 'relative' }} data-dropdown-container>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
                <span style={{ fontSize: 14 }}>{form.payment_account_type === 'bank' ? t('equipmentExpenses.edit.bankLabel') : t('equipmentExpenses.edit.safeLabel')}</span>
                <div data-dropdown-toggle onClick={() => setAccountDropdownOpen((prev) => !prev)} style={{ ...dropdownDisplayStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                  <span>
                    {getSelectedAccount()
                      ? (isRTL
                      ? getSelectedAccount().name_ar || getSelectedAccount().name_en || `#${form.payment_account_id}`
                          : getSelectedAccount().name_en || getSelectedAccount().name_ar || `#${form.payment_account_id}`)
                      : form.payment_account_type === 'bank' ? t('equipmentExpenses.edit.selectBank') : t('equipmentExpenses.edit.selectSafe')}
                  </span>
                  <span style={{ fontSize: 12 }}>▼</span>
                </div>
                <DropdownWithSearch
                  isOpen={accountDropdownOpen}
                  searchTerm={accountSearchTerm}
                  onSearchChange={setAccountSearchTerm}
                  searchInputRef={accountSearchInputRef}
                  items={getAccountOptions()}
                  pagination={getAccountPagination()}
                  onPageChange={setAccountPage}
                  onClose={() => setAccountDropdownOpen(false)}
                  onSelect={(item) => {
                    setForm((prev) => ({ ...prev, payment_account_id: String(item.id) }));
                    setAccountDropdownOpen(false);
                    setAccountSearchTerm('');
                    setAccountPage(1);
                  }}
                  label={form.payment_account_type === 'bank' ? t('equipmentExpenses.edit.bank') : t('equipmentExpenses.edit.safe')}
                />
              </label>
            </div>
          )}

          <div style={{ position: 'relative' }} data-dropdown-container>
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 16 }}>
              <span style={{ fontSize: 14 }}>{t('equipmentExpenses.edit.project')}</span>
              <div data-dropdown-toggle onClick={() => setProjectDropdownOpen((prev) => !prev)} style={{ ...dropdownDisplayStyle, direction: isRTL ? 'rtl' : 'ltr', textAlign: isRTL ? 'right' : 'left' }}>
                <span>
                  {form.project_id
                    ? (isRTL
                    ? projectOptions.find((p) => String(p.id) === String(form.project_id))?.name_ar ||
                      projectOptions.find((p) => String(p.id) === String(form.project_id))?.name_en ||
                          `${t('equipmentExpenses.tableHeaders.project')} #${form.project_id}`
                        : projectOptions.find((p) => String(p.id) === String(form.project_id))?.name_en ||
                          projectOptions.find((p) => String(p.id) === String(form.project_id))?.name_ar ||
                          `${t('equipmentExpenses.tableHeaders.project')} #${form.project_id}`)
                    : t('equipmentExpenses.edit.selectProject')}
                </span>
                <span style={{ fontSize: 12 }}>▼</span>
              </div>
              <DropdownWithSearch
                isOpen={projectDropdownOpen}
                searchTerm={projectSearchTerm}
                onSearchChange={setProjectSearchTerm}
                searchInputRef={projectSearchInputRef}
                items={projectOptions}
                pagination={projectPagination}
                onPageChange={setProjectPage}
                onClose={() => setProjectDropdownOpen(false)}
                onSelect={(item) => {
                  setForm((prev) => ({ ...prev, project_id: String(item.id) }));
                  setProjectDropdownOpen(false);
                  setProjectSearchTerm('');
                  setProjectPage(1);
                }}
                label={t('equipmentExpenses.tableHeaders.project')}
              />
            </label>
          </div>

          <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <span>{t('equipmentExpenses.edit.notes')}</span>
            <textarea
              name="notes"
              value={form.notes}
              onChange={handleChange}
              rows={4}
              style={{
                padding: 12,
                borderRadius: 8,
                border: '1px solid #333',
                backgroundColor: '#1a1f2e',
                color: 'white',
                resize: 'vertical',
                direction: isRTL ? 'rtl' : 'ltr',
                textAlign: isRTL ? 'right' : 'left',
              }}
            />
          </label>

          <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexDirection: isRTL ? 'row' : 'row-reverse' }}>
            <button type="submit" style={submitButtonStyle} disabled={isSubmitting}>
              {isSubmitting ? t('equipmentExpenses.edit.saving') : t('equipmentExpenses.edit.save')}
            </button>
            <button type="button" style={secondaryButtonStyle} onClick={() => navigate('/equipment-expenses')}>
              {t('equipmentExpenses.edit.cancel')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const Input = ({ label, isRTL, ...rest }) => (
  <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
    <span style={{ fontSize: 14 }}>{label}</span>
    <input
      {...rest}
      style={{
        padding: 12,
        borderRadius: 8,
        border: '1px solid #333',
        backgroundColor: '#1a1f2e',
        color: 'white',
        direction: isRTL !== undefined ? (isRTL ? 'rtl' : 'ltr') : 'inherit',
        textAlign: isRTL !== undefined ? (isRTL ? 'right' : 'left') : 'inherit',
      }}
    />
  </label>
);

const pageStyle = {
  padding: 30,
  backgroundColor: '#14263b',
  minHeight: 'calc(100vh - 80px)',
  color: 'white',
  display: 'flex',
  justifyContent: 'center',
  width: '100%',
  boxSizing: 'border-box',
};

const cardStyle = {
  width: '100%',
  maxWidth: 'calc(100vw - 320px)', // Adjust based on sidebar width
  backgroundColor: '#1b3350',
  borderRadius: 12,
  padding: 24,
  border: '1px solid #333',
  boxSizing: 'border-box',
};

const gridStyle = {
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
  gap: 16,
};

const submitButtonStyle = {
  backgroundColor: '#AC2000',
  color: 'white',
  border: 'none',
  padding: '12px 18px',
  borderRadius: 8,
  cursor: 'pointer',
  minWidth: 120,
};

const secondaryButtonStyle = {
  backgroundColor: 'transparent',
  color: 'white',
  border: '1px solid #444',
  padding: '12px 18px',
  borderRadius: 8,
  cursor: 'pointer',
  minWidth: 120,
};

const filterInputStyle = {
  width: '100%',
  padding: 12,
  backgroundColor: '#1a1f2e',
  border: '1px solid #333',
  borderRadius: 8,
  color: 'white',
};

const dropdownDisplayStyle = {
  width: '100%',
  padding: '12px 14px',
  backgroundColor: '#1a1f2e',
  border: '1px solid #333',
  borderRadius: 8,
  color: 'white',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  cursor: 'pointer',
  textAlign: 'right',
};

const dropdownMenuStyle = {
  position: 'absolute',
  top: 'calc(100% + 4px)',
  left: 0,
  right: 0,
  backgroundColor: '#1a1f2e',
  border: '1px solid #333',
  borderRadius: 8,
  zIndex: 20,
  padding: 12,
  boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
};

const dropdownSearchStyle = {
  width: '100%',
  padding: 10,
  borderRadius: 6,
  border: '1px solid #333',
  backgroundColor: '#14263b',
  color: 'white',
  marginBottom: 10,
};

const dropdownEmptyStyle = {
  padding: 12,
  textAlign: 'center',
  color: '#ccc',
  fontSize: 13,
};

const dropdownPaginationButtonStyle = (disabled) => ({
  padding: '6px 10px',
  borderRadius: 6,
  border: '1px solid #333',
  backgroundColor: disabled ? '#333' : '#AC2000',
  color: 'white',
  cursor: disabled ? 'not-allowed' : 'pointer',
  opacity: disabled ? 0.6 : 1,
});

const DropdownWithSearch = ({
  isOpen,
  searchTerm,
  onSearchChange,
  searchInputRef,
  items,
  pagination,
  onPageChange,
  onSelect,
  onClose,
  label,
}) => {
  const { t } = useTranslation();
  
  useEffect(() => {
    if (isOpen && searchInputRef?.current) {
      const timer = setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
      return () => clearTimeout(timer);
    }
  }, [isOpen, searchTerm, items, searchInputRef]);

  if (!isOpen) return null;

  return (
    <div style={dropdownMenuStyle} onClick={(e) => e.stopPropagation()}>
      <div style={{ padding: 8 }}>
        <input
          ref={searchInputRef}
          type="text"
          placeholder={label === t('equipmentExpenses.tableHeaders.equipment')
            ? t('equipmentExpenses.dropdown.searchEquipment')
            : label === t('equipmentExpenses.tableHeaders.project')
            ? t('equipmentExpenses.dropdown.searchProject')
            : `Search for ${label}`}
          value={searchTerm}
          onChange={(e) => {
            onSearchChange(e.target.value);
            setTimeout(() => {
              searchInputRef.current?.focus();
            }, 0);
          }}
          onFocus={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          autoFocus
          style={{ ...dropdownSearchStyle, direction: i18n.language === 'ar' ? 'rtl' : 'ltr', textAlign: i18n.language === 'ar' ? 'right' : 'left' }}
        />
      </div>
      {pagination && (pagination.last_page > 1 || (pagination.last_page === 1 && pagination.current_page === 1)) && (
        <div style={{ padding: '8px', borderTop: '1px solid #333', background: '#14263b' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
            <span style={{ color: '#ccc', fontSize: 11, direction: i18n.language === 'ar' ? 'rtl' : 'ltr' }}>
              {t('equipmentExpenses.dropdown.page')} {pagination.current_page} {t('equipmentExpenses.dropdown.of')} {pagination.last_page}
            </span>
          </div>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>««</button>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page > 1) onPageChange(pagination.current_page - 1); }} disabled={pagination.current_page === 1} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === 1 ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === 1 ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === 1 ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>‹</button>
            {(() => {
              const pages = [];
              const current = pagination.current_page;
              const last = pagination.last_page;
              const showPages = 5;
              let start = Math.max(1, current - Math.floor(showPages / 2));
              let end = Math.min(last, start + showPages - 1);
              if (end - start < showPages - 1) start = Math.max(1, end - showPages + 1);
              for (let i = start; i <= end; i++) pages.push(i);
              return pages.map((page) => (
                <button key={page} type="button" onClick={(e) => { e.stopPropagation(); onPageChange(page); }} style={{ padding: '4px 8px', backgroundColor: page === current ? '#AC2000' : '#1a1f2e', color: 'white', border: page === current ? '1px solid #AC2000' : '1px solid #444', borderRadius: '4px', cursor: 'pointer', fontSize: 10, fontWeight: page === current ? 'bold' : 'normal', minWidth: '28px' }}>{page}</button>
              ));
            })()}
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.current_page + 1); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>›</button>
            <button type="button" onClick={(e) => { e.stopPropagation(); if (pagination.current_page < pagination.last_page) onPageChange(pagination.last_page); }} disabled={pagination.current_page === pagination.last_page} style={{ padding: '4px 8px', backgroundColor: pagination.current_page === pagination.last_page ? '#2a2a2a' : '#1a1f2e', color: pagination.current_page === pagination.last_page ? '#666' : 'white', border: '1px solid #444', borderRadius: '4px', cursor: pagination.current_page === pagination.last_page ? 'not-allowed' : 'pointer', fontSize: 10, fontWeight: 'bold', minWidth: '28px' }}>»»</button>
          </div>
        </div>
      )}
      <div style={{ maxHeight: 300, overflowY: 'auto', paddingBottom: 4 }}>
        {(Array.isArray(items) ? items : []).length === 0 ? (
          <div style={{ ...dropdownEmptyStyle, direction: i18n.language === 'ar' ? 'rtl' : 'ltr', textAlign: i18n.language === 'ar' ? 'right' : 'left' }}>
            {t('equipmentExpenses.dropdown.noResults')}
          </div>
        ) : (
          (Array.isArray(items) ? items : []).map((item) => (
            <div
              key={item.id}
              style={{ padding: '10px 12px', borderTop: '1px solid #333', cursor: 'pointer', direction: i18n.language === 'ar' ? 'rtl' : 'ltr', textAlign: i18n.language === 'ar' ? 'right' : 'left' }}
              onClick={() => {
                onSelect(item);
                onClose?.();
              }}
            >
              {formatEntity(item)}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const formatEntity = (entity) => {
  if (!entity) return '—';
  const code = entity.code ? `${entity.code} - ` : '';
  const isRTL = i18n.language === 'ar';
  const name = isRTL 
    ? (entity.name_ar || entity.name_en || '')
    : (entity.name_en || entity.name_ar || '');
  return `${code}${name}` || `#${entity.id}`;
};

export default EditEquipmentExpenses;
